import React from "react";

/* ------------------------------------------------------------------
   Presentation helpers shared by the cabinet quote pages (dashboard, new
   quote, workspace, admin, render enhancer, customer portal). Pure styling —
   no data or behavior lives here.
------------------------------------------------------------------ */

// Labels sit above inputs; inputs use the themed .app-input surface.
export const CQ_LABEL = "mb-1.5 block text-sm font-medium";
export const CQ_FIELD = "app-input w-full rounded-xl border px-3 py-2 text-sm";
export const CQ_FIELD_SM = "app-input rounded-lg border px-2.5 py-1.5 text-sm";

// Translucent status tints that read on white, slate and glass surfaces.
// Literal strings so Tailwind keeps them.
const STATUS_STYLES = {
  draft: "border-slate-500/25 bg-slate-500/10 text-slate-700 dark:text-slate-200",
  in_review: "border-amber-500/30 bg-amber-500/15 text-amber-800 dark:text-amber-200",
  pending_customer: "border-sky-500/30 bg-sky-500/15 text-sky-800 dark:text-sky-200",
  approved: "border-emerald-500/30 bg-emerald-500/15 text-emerald-800 dark:text-emerald-200",
  rejected: "border-rose-500/30 bg-rose-500/15 text-rose-800 dark:text-rose-200",
  archived: "border-slate-500/25 bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

export const cqStatusClass = (status) =>
  STATUS_STYLES[String(status || "").trim()] || STATUS_STYLES.draft;

const NOTICE_TONES = {
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200",
  rose: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  sky: "border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-200",
  slate: "border-[color:var(--app-surface-border)] bg-[color:var(--app-surface-bg)]",
};

/** Tinted callout for warnings, errors and confirmations. */
export const Notice = ({ tone = "amber", className = "", children, ...rest }) => (
  <div
    className={`rounded-xl border px-3.5 py-2.5 text-sm ${NOTICE_TONES[tone] || NOTICE_TONES.amber} ${className}`}
    {...rest}
  >
    {children}
  </div>
);

/**
 * iOS-style segmented control (mirrors the MUI ToggleButtonGroup styling in
 * theme/MuiThemeBridge.js). An option may pass `activeClass` to tint its
 * selected state, e.g. the customer-facing chat channel.
 */
export const Segmented = ({ options, value, onChange, ariaLabel, className = "" }) => (
  <div className={`max-w-full overflow-x-auto ${className}`}>
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex gap-0.5 rounded-[0.8rem] bg-[color:var(--app-hover)] p-[3px]"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            disabled={option.disabled}
            aria-pressed={active}
            className={`inline-flex min-h-[2.25rem] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[0.6rem] px-3.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              active
                ? option.activeClass ||
                  "bg-white text-[color:var(--app-text)] shadow-[0_1px_3px_rgba(15,23,42,0.12)] dark:bg-white/[0.14] dark:shadow-none"
                : option.inactiveClass || "app-muted hover:text-[color:var(--app-text)]"
            }`}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);
